import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://api.dev1.auth.joeandalese.com/v1";

function groupByYear(photos) {
  return photos.reduce((acc, photo) => {
    const year = photo.year || "Unknown";
    if (!acc[year]) acc[year] = [];
    acc[year].push(photo);
    return acc;
  }, {});
}

function PhotosPage() {
  const [photos, setPhotos] = useState([]);
  const [urls, setUrls] = useState({});
  const [lightboxKey, setLightboxKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userType, setUserType] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [hoveredKey, setHoveredKey] = useState(null);
  const [openMenuKey, setOpenMenuKey] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [bulkMenuOpen, setBulkMenuOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  // Close any open dropdown on outside click
  useEffect(() => {
    const handler = () => { setOpenMenuKey(null); setBulkMenuOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    fetch(`${API_BASE}/me`, { credentials: "include" })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => setUserType(data.userType))
      .catch(status => { if (status === 401) navigate("/"); });
  }, [navigate]);

  const fetchPhotos = useCallback(async () => {
    setLoading(true);
    const resp = await fetch(`${API_BASE}/photos`, { credentials: "include" });
    const data = await resp.json();
    setPhotos(data);
    setLoading(false);
  }, []);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  useEffect(() => {
    async function fetchUrls() {
      const newUrls = {};
      const batchSize = 8;

      for (let i = 0; i < photos.length; i += batchSize) {
        const batch = photos.slice(i, i + batchSize);
        const results = await Promise.allSettled(
          batch.map(async (photo) => {
            const resp = await fetch(`${API_BASE}/photos/${photo.key}`, { credentials: "include" });
            if (!resp.ok) throw new Error(`Photo URL request failed for ${photo.key}`);
            const data = await resp.json();
            if (!data.url) throw new Error(`No URL returned for ${photo.key}`);
            return { key: photo.key, url: data.url };
          })
        );

        for (const result of results) {
          if (result.status === "fulfilled") {
            newUrls[result.value.key] = result.value.url;
          }
        }
      }

      setUrls(newUrls);
    }
    if (photos.length) fetchUrls();
  }, [photos]);

  const handlePhotoClick = (e, key) => {
    if (e.shiftKey) {
      e.preventDefault();
      setSelectedKeys(prev => {
        const next = new Set(prev);
        next.has(key) ? next.delete(key) : next.add(key);
        return next;
      });
    } else {
      setLightboxKey(key);
    }
  };

  const deleteKeys = useCallback(async (keys) => {
    setDeleting(true);
    const arr = Array.from(keys);
    await Promise.all(arr.map(key =>
      fetch(`${API_BASE}/photos/${key}`, { method: "DELETE", credentials: "include" })
    ));
    setPhotos(prev => prev.filter(p => !keys.has(p.key)));
    setUrls(prev => { const next = { ...prev }; arr.forEach(k => delete next[k]); return next; });
    setSelectedKeys(new Set());
    setOpenMenuKey(null);
    setBulkMenuOpen(false);
    setDeleting(false);
  }, []);

  const handleDeleteSingle = (e, key) => {
    e.stopPropagation();
    setOpenMenuKey(null);
    if (!window.confirm("Delete this photo?")) return;
    deleteKeys(new Set([key]));
  };

  const handleDeleteSelected = (e) => {
    e.stopPropagation();
    setBulkMenuOpen(false);
    if (!window.confirm(`Delete ${selectedKeys.size} photo${selectedKeys.size > 1 ? "s" : ""}?`)) return;
    deleteKeys(selectedKeys);
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    e.target.value = "";

    const images = files.filter(f => f.type !== "application/zip" && !f.name.endsWith(".zip"));
    const zips = files.filter(f => f.type === "application/zip" || f.name.endsWith(".zip"));

    setUploading(true);
    const parts = [];
    if (images.length) parts.push(`${images.length} photo${images.length > 1 ? "s" : ""}`);
    if (zips.length) parts.push(`${zips.length} takeout ZIP${zips.length > 1 ? "s" : ""}`);
    setUploadStatus(`Uploading ${parts.join(" and ")}...`);

    let succeeded = 0, failed = 0, takeoutCount = 0;

    for (const file of images) {
      try {
        const params = new URLSearchParams({ filename: file.name, contentType: file.type });
        const urlResp = await fetch(`${API_BASE}/upload-url?${params}`, { credentials: "include" });
        if (!urlResp.ok) { failed++; continue; }
        const { uploadUrl } = await urlResp.json();
        const putResp = await fetch(uploadUrl, { method: "PUT", headers: { "Content-Type": file.type }, body: file });
        putResp.ok ? succeeded++ : failed++;
      } catch { failed++; }
    }

    for (const file of zips) {
      try {
        const urlResp = await fetch(`${API_BASE}/takeout-upload-url`, { credentials: "include" });
        if (!urlResp.ok) { failed++; continue; }
        const { uploadUrl } = await urlResp.json();
        const putResp = await fetch(uploadUrl, { method: "PUT", headers: { "Content-Type": "application/zip" }, body: file });
        putResp.ok ? takeoutCount++ : failed++;
      } catch { failed++; }
    }

    setUploading(false);
    const statusParts = [];
    if (succeeded > 0) statusParts.push(`${succeeded} photo${succeeded > 1 ? "s" : ""} uploaded`);
    if (failed > 0) statusParts.push(`${failed} failed`);
    if (takeoutCount > 0) statusParts.push(`takeout processing — new photos will appear in a few minutes`);
    setUploadStatus(statusParts.join(". ") + ".");

    setTimeout(() => setUploadStatus(null), takeoutCount > 0 ? 10000 : 4000);
    if (succeeded > 0) fetchPhotos();
  };

  const canModify = userType === "admin" || userType === "contributor";
  const grouped = groupByYear(photos);
  const showTakeoutUploadingIndicator = uploading && (uploadStatus || "").includes("takeout ZIP");

  return (
    <div style={{ maxWidth: 900, margin: "40px auto", padding: 24 }}>
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <h1 style={{ margin: 0 }}>Photo Gallery</h1>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {/* Bulk action bubble */}
          {selectedKeys.size > 0 && (
            <div
              style={{ display: "flex", alignItems: "center", gap: 8, background: "#e3f2fd", padding: "6px 12px", borderRadius: 6, border: "1px solid #90caf9" }}
              onMouseDown={e => e.stopPropagation()}
            >
              <span style={{ fontSize: 14, color: "#1565c0", fontWeight: 500 }}>
                {selectedKeys.size} selected
              </span>
              <div style={{ position: "relative" }}>
                <button
                  onClick={(e) => { e.stopPropagation(); setBulkMenuOpen(o => !o); }}
                  style={{ padding: "3px 10px", background: "#1976d2", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", fontWeight: 700, fontSize: 18, lineHeight: 1 }}
                  title="Actions for selected"
                >
                  ⋮
                </button>
                {bulkMenuOpen && (
                  <div style={{ ...menuStyle, top: 30, right: 0 }}>
                    <button onClick={handleDeleteSelected} disabled={deleting} style={deleteItemStyle}>
                      {deleting ? "Deleting..." : `Delete ${selectedKeys.size} photo${selectedKeys.size > 1 ? "s" : ""}`}
                    </button>
                  </div>
                )}
              </div>
              <button
                onClick={() => setSelectedKeys(new Set())}
                style={{ background: "none", border: "none", color: "#888", cursor: "pointer", fontSize: 16, padding: 0, lineHeight: 1 }}
                title="Clear selection"
              >
                ✕
              </button>
            </div>
          )}

          {/* Upload */}
          {canModify && (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {uploadStatus && (
                <span className="takeout-upload-indicator" style={{ fontSize: 14, color: uploadStatus.includes("failed") ? "#d32f2f" : uploadStatus.includes("processing") ? "#1565c0" : "#2e7d32" }}>
                  {showTakeoutUploadingIndicator && <span className="takeout-upload-spinner" aria-hidden="true" />}
                  {uploadStatus}
                </span>
              )}
              <input ref={fileInputRef} type="file" accept="image/*,.zip" multiple style={{ display: "none" }} onChange={handleFileChange} />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                style={{ padding: "8px 18px", background: uploading ? "#e0e0e0" : "#1976d2", color: uploading ? "#aaa" : "#fff", border: "none", borderRadius: 4, cursor: uploading ? "not-allowed" : "pointer", fontWeight: 500 }}
              >
                {uploading ? "Uploading..." : "Upload Photos"}
              </button>
            </div>
          )}
        </div>
      </div>

      <p style={{ fontSize: 12, color: "#bbb", marginTop: 0, marginBottom: 20 }}>
        Hold Shift and click to select multiple photos.
      </p>

      {loading && <div>Loading photos...</div>}
      {!loading && photos.length === 0 && <div style={{ color: "#888" }}>No photos yet.</div>}

      {!loading && Object.keys(grouped).sort((a, b) => b.localeCompare(a)).map(year => (
        <div key={year} style={{ marginBottom: 32 }}>
          <h2>{year}</h2>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
            {grouped[year].map(photo => {
              const isSelected = selectedKeys.has(photo.key);
              const menuOpen = openMenuKey === photo.key;
              return (
                <div
                  key={photo.key}
                  style={{ position: "relative", cursor: "pointer", userSelect: "none" }}
                  onClick={(e) => handlePhotoClick(e, photo.key)}
                  onMouseEnter={() => setHoveredKey(photo.key)}
                  onMouseLeave={() => setHoveredKey(null)}
                >
                  {urls[photo.key] ? (
                    <img
                      src={urls[photo.key]}
                      alt={photo.key}
                      style={{
                        width: 120, height: 120, objectFit: "cover", borderRadius: 8, display: "block",
                        boxShadow: isSelected ? "0 0 0 3px #1976d2" : "0 2px 8px #ccc",
                        opacity: isSelected ? 0.8 : 1,
                      }}
                    />
                  ) : (
                    <div style={{ width: 120, height: 120, background: "#eee", borderRadius: 8 }} />
                  )}

                  {/* Selection checkmark */}
                  {isSelected && (
                    <div style={{ position: "absolute", top: 5, left: 5, width: 20, height: 20, borderRadius: "50%", background: "#1976d2", display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                      <span style={{ color: "#fff", fontSize: 11, lineHeight: 1 }}>✓</span>
                    </div>
                  )}

                  {/* Per-photo hamburger menu */}
                  {canModify && (hoveredKey === photo.key || menuOpen) && (
                    <div
                      style={{ position: "absolute", top: 4, right: 4 }}
                      onMouseDown={e => e.stopPropagation()}
                    >
                      <button
                        onClick={(e) => { e.stopPropagation(); setOpenMenuKey(k => k === photo.key ? null : photo.key); }}
                        style={{ width: 26, height: 26, background: "rgba(0,0,0,0.55)", color: "#fff", border: "none", borderRadius: "50%", cursor: "pointer", fontSize: 16, fontWeight: 700, padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}
                        title="Photo options"
                      >
                        ⋮
                      </button>
                      {menuOpen && (
                        <div style={{ ...menuStyle, top: 30, right: 0 }}>
                          <button onClick={(e) => handleDeleteSingle(e, photo.key)} disabled={deleting} style={deleteItemStyle}>
                            {deleting ? "Deleting..." : "Delete"}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  <div style={{ fontSize: 12, marginTop: 4, color: "#666" }}>{photo.last_modified?.slice(0, 10)}</div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {lightboxKey && (
        <div
          style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
          onClick={() => setLightboxKey(null)}
        >
          <img src={urls[lightboxKey]} alt={lightboxKey} style={{ maxWidth: "90vw", maxHeight: "90vh", borderRadius: 8, boxShadow: "0 2px 24px #000" }} />
        </div>
      )}
    </div>
  );
}

const menuStyle = {
  position: "absolute",
  background: "#fff",
  border: "1px solid #e0e0e0",
  borderRadius: 6,
  boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
  minWidth: 150,
  zIndex: 200,
  overflow: "hidden",
};

const deleteItemStyle = {
  display: "block",
  width: "100%",
  padding: "10px 16px",
  background: "none",
  border: "none",
  textAlign: "left",
  cursor: "pointer",
  fontSize: 14,
  color: "#d32f2f",
  fontWeight: 500,
};

export default PhotosPage;
